/**
 * Builds the groundworksItemLibrary payload object for BOQ submission.
 * For every table, maps each field key (from its reference mapping) to the
 * matching item library entry's resourceCode and costCodeCode.
 */

import { ItemLibraryItem, ItemLibraryResponse } from "../api/itemLibrary";
import { ALL_REFERENCES as CUT_FILLS_REFERENCES } from "./cutFillsReferenceMapping";
import { FOUNDATIONS_REFERENCES, CELCON_GENERAL_CODE_OVERRIDES, CONCRETE_STRIP_CODE_MAP, CONCRETE_FIELD_PREFIX_CODES } from "./foundationsReferenceMapping";
import { DRAINAGE_REFERENCES } from "./drainageReferenceMapping";
import { PLOT_DRIVES_REFERENCES } from "./plotDrivesReferenceMapping";
import { transformPlotDrivesData, PLOT_DRIVES_REVERSE_MAPPING, PLOT_DRIVES_COLUMN_MAPPING } from "./plotDrivesColumnMapping";
import { EVALUATE_CODES } from "./constants";
import { GARDEN_REFERENCES } from "./gardenReferenceMapping";
import { PLOT_PAVINGS_REFERENCES } from "./plotPavingsReferenceMapping";
import { SCAFFOLD_REFERENCES } from "./scaffoldReferenceMapping";
import { PLOT_SERVICE_REFERENCES } from "./plotServiceReferenceMapping";
import { RETAINING_WALLS_REFERENCES, resolveScreenWallsCode, resolveEOPillarCode } from "./retainingWallsReferenceMapping";
import { GEO_TOBER_WALL_REFERENCES } from "./geoToberWallReferenceMapping";

export interface ItemLibraryResource {
  resourceCode: string;
  costCode: string;
  nettRate: number;
}

type TableItemLibrary = Record<string, ItemLibraryResource>;

export interface GroundworksTableDataMap {
  cutFills?: any[];
  foundations?: any[];
  drainage?: any[];
  plotDrives?: any[];
  garden?: any[];
  plotPavings?: any[];
  scaffold?: any[];
  plotService?: any[];
  retainingWalls?: any[];
  geoToberWall?: any[];
}

function hasQtyInAnyRow(tableData: any[], fieldKey: string): boolean {
  return tableData.some((row) => {
    const val = row[fieldKey];
    return val !== undefined && val !== null && val !== 0 && val !== "";
  });
}

function buildResourceCodeMap(
  items: ItemLibraryItem[],
): Map<string, ItemLibraryItem> {
  const map = new Map<string, ItemLibraryItem>();
  items.forEach((item) => {
    const code = item.resourceCode?.trim().toUpperCase();
    if (code) map.set(code, item);
  });
  return map;
}


/**
 * Resolves a resource code string against the item library. If the input
 * contains `+` (e.g. "ALP031A+GWALP004"), each component is looked up and
 * their nettRates are summed; the costCode is taken from the first matched
 * component. The original combined code string is returned as-is.
 */
function resolveResourceCode(
  code: string,
  resourceCodeMap: Map<string, ItemLibraryItem>,
): { resourceCode: string; costCode: string; nettRate: number } {
  const parts = code.split('+').map((p) => p.trim()).filter(Boolean);
  if (parts.length <= 1) {
    const item = resourceCodeMap.get(code.trim().toUpperCase());
    return {
      resourceCode: code,
      costCode: item?.costCodeCode || "",
      nettRate: item?.nettRate || 0,
    };
  }
  let summedRate = 0;
  let firstCostCode = "";
  for (const part of parts) {
    const item = resourceCodeMap.get(part.toUpperCase());
    summedRate += item?.nettRate || 0;
    if (!firstCostCode && item?.costCodeCode) firstCostCode = item.costCodeCode;
  }
  return { resourceCode: code, costCode: firstCostCode, nettRate: summedRate };
}

function buildTableItemLibrary(
  references: Record<string, { code?: string; dimensionValueKey?: string }>,
  resourceCodeMap: Map<string, ItemLibraryItem>,
  tableData?: any[],
  codesOnly?: boolean,
  anyRowSufficient?: boolean,
  dynamicCodeResolver?: (fieldName: string, ref: { code?: string; dimensionValueKey?: string }) => string | undefined,
): TableItemLibrary {
  const result: TableItemLibrary = {};
  const hasData = tableData && tableData.length > 0;
  Object.entries(references).forEach(([fieldName, reference]) => {
    if (hasData) {
      const skip = anyRowSufficient ? false : !hasQtyInAnyRow(tableData, fieldName);
      if (skip) return;
    }

    // Try dynamic code resolution first (for fields with dimensionValueKey or empty code)
    const dynamicCode = dynamicCodeResolver?.(fieldName, reference);
    if (dynamicCode) {
      result[fieldName] = resolveResourceCode(dynamicCode, resourceCodeMap);
      return;
    }

    if (!reference.code) {
      if (!codesOnly) {
        result[fieldName] = { resourceCode: "", costCode: "", nettRate: 0 };
      }
      return;
    }
    result[fieldName] = resolveResourceCode(reference.code, resourceCodeMap);
  });
  return result;
}

export interface GroundworksContextData {
  drainageDimensionValues?: Record<string, string>;
  foundationsPrefixValues?: Record<string, string>;
  cutFillsPrefixValues?: Record<string, string>;
  plotDrivesDimensionValues?: Record<string, string>;
  plotDrivesCodeOverrides?: Record<string, string>;
  substructureBlockType?: string;
  plotServiceConfig?: {
    plotServicesApartmentsMultiple?: number;
    serviceConnectionApartmentsMultiple?: number;
  };
  screenWallsLabel?: string;
  eoPillarLabel?: string;
}

function applyPlotServiceApartmentsMultiple(
  library: TableItemLibrary,
  config?: GroundworksContextData['plotServiceConfig'],
): TableItemLibrary {
  if (!config) return library;
  const result = { ...library };
  const plotMultiple = config.plotServicesApartmentsMultiple ?? 1;
  const scMultiple = config.serviceConnectionApartmentsMultiple ?? 1;
  if (result.apartments) {
    result.apartments = { ...result.apartments, nettRate: result.apartments.nettRate * plotMultiple };
  }
  if (result.serviceConnectionApartments) {
    result.serviceConnectionApartments = { ...result.serviceConnectionApartments, nettRate: result.serviceConnectionApartments.nettRate * scMultiple };
  }
  return result;
}

/**
 * Dev-mode code picks win over the static/dynamic code. Applied after the
 * build (and regardless of quantity) so a pick survives save → reload even
 * before any quantity is entered against that column.
 */
function applyPlotDrivesCodeOverrides(
  library: TableItemLibrary,
  overrides: Record<string, string> | undefined,
  resourceCodeMap: Map<string, ItemLibraryItem>,
): TableItemLibrary {
  if (!overrides) return library;
  const result = { ...library };
  Object.entries(overrides).forEach(([colKey, code]) => {
    const fieldName = PLOT_DRIVES_COLUMN_MAPPING[colKey];
    if (fieldName && code) result[fieldName] = resolveResourceCode(code, resourceCodeMap);
  });
  return result;
}

export function buildGroundworksItemLibrary(
  itemLibraryData?: ItemLibraryResponse,
  tableDataMap?: GroundworksTableDataMap,
  groundworksContext?: GroundworksContextData,
): Record<string, TableItemLibrary> {
  // The API returns items as a plain array; fall back to .items for wrapper format
  const items: ItemLibraryItem[] = Array.isArray(itemLibraryData)
    ? (itemLibraryData as unknown as ItemLibraryItem[])
    : (itemLibraryData?.items || []);
  const resourceCodeMap = buildResourceCodeMap(items);

  // Resolver for drainage dynamic fields (dimensionValueKey → code from drainageDimensionValues)
  const drainageDynamicResolver = (_fieldName: string, ref: { code?: string; dimensionValueKey?: string }) => {
    if (ref.dimensionValueKey && groundworksContext?.drainageDimensionValues) {
      return groundworksContext.drainageDimensionValues[ref.dimensionValueKey];
    }
    return undefined;
  };

  // Resolver for foundations dynamic fields (bbFloorHouse → code from foundationsPrefixValues,
  // Celcon-general → block-type-specific GWALPS codes, Terram → combined pileMat code,
  // concrete type fields → RMC<xxx> code, optionally combined with GWALP013 for strip).
  const foundationsDynamicResolver = (fieldName: string, ref: { code?: string }) => {
    if (groundworksContext?.substructureBlockType === 'Celcon - general') {
      const celconCode = CELCON_GENERAL_CODE_OVERRIDES[fieldName];
      if (celconCode) return celconCode;
    }
    // Terram on Piling Mat: pileMat resource code becomes the combined string
    // "ALP031A+GWALP004" so resolveResourceCode sums both nettRates.
    if (fieldName === "pileMat" && groundworksContext?.foundationsPrefixValues?.terram === 'true') {
      return 'ALP031A+GWALP004';
    }
    // Concrete-type fields: map the selected concrete prefix (GEN1, RC28/35, …) to
    // its RMC code, then combine with the field's placement prefix
    // (gen1ConcreteToStrip→GWALP013, rc35ConcreteToPiled→GWALP019a,
    //  rc35ConcreteToVibro→GWALP025) so resolveResourceCode sums both nettRates.
    if (CONCRETE_FIELD_PREFIX_CODES[fieldName]) {
      const concreteType = groundworksContext?.foundationsPrefixValues?.[fieldName];
      const rmc = concreteType ? CONCRETE_STRIP_CODE_MAP[concreteType] : undefined;
      if (rmc) {
        return `${CONCRETE_FIELD_PREFIX_CODES[fieldName]}+${rmc}`;
      }
    }
    if (fieldName === "bbFloorHouse" && ref.code === "" && groundworksContext?.foundationsPrefixValues?.bbFloorHouse) {
      return groundworksContext.foundationsPrefixValues.bbFloorHouse;
    }
    // bbFloorGarage: the Configure modal Floors→Garage selector lets the user
    // pick GWALPF006 / GWALPF012 (or any other GWALPF code in future). The
    // FOUNDATIONS_REFERENCES entry has no static fallback, so when nothing is
    // selected the field resolves to an empty resource code / zero rate.
    if (fieldName === "bbFloorGarage" && groundworksContext?.foundationsPrefixValues?.bbFloorGarage) {
      return groundworksContext.foundationsPrefixValues.bbFloorGarage;
    }
    return undefined;
  };

  // Reverse map: EVALUATE_CODES label → resource code (built once per call)
  const evaluateLabelToCode: Record<string, string> = {};
  Object.values(EVALUATE_CODES).forEach(({ code, label }) => {
    evaluateLabelToCode[label.trim().toLowerCase()] = code;
  });

  // Resolver for plot drives dynamic fields.
  // Dimension values can be:
  //   • An ALP/GWALP code directly (e.g. "ALP048")        → return as-is
  //   • An EVALUATE_CODES label (e.g. "100mm thick AC 32mm dense bin") → reverse-lookup to code
  //   • A numeric thickness (e.g. "100", "300")            → no code, return undefined
  const plotDrivesDynamicResolver = (fieldName: string, _ref: { code?: string }) => {
    const dimValues = groundworksContext?.plotDrivesDimensionValues;
    if (!dimValues) return undefined;
    const colKey = PLOT_DRIVES_REVERSE_MAPPING[fieldName];
    if (!colKey) return undefined;
    const value = dimValues[colKey];
    if (!value) return undefined;
    const trimmed = value.trim();
    if (trimmed.startsWith('ALP') || trimmed.startsWith('GWALP')) return trimmed;
    // Try label-to-code reverse lookup (for DB Base/Binder fields that store full labels)
    const fromLabel = evaluateLabelToCode[trimmed.toLowerCase()];
    if (fromLabel) return fromLabel;
    return undefined;
  };

  // Resolver for Retaining Walls dynamic fields (screenWalls/screenWallsEO →
  // code from each field's own independent Free Issue/M&J Supply Configure
  // toggle selection).
  const retainingWallsDynamicResolver = (_fieldName: string, ref: { code?: string; dimensionValueKey?: string }) => {
    if (ref.dimensionValueKey === 'screenWalls') {
      return resolveScreenWallsCode(groundworksContext?.screenWallsLabel);
    }
    if (ref.dimensionValueKey === 'screenWallsEO') {
      return resolveEOPillarCode(groundworksContext?.eoPillarLabel);
    }
    return undefined;
  };

  // plotDrivesTable stores data as column keys (col1, col4 …); transform to semantic
  // field names so hasQtyInAnyRow can correctly match against PLOT_DRIVES_REFERENCES keys.
  const plotDrivesSemanticData = tableDataMap?.plotDrives
    ? transformPlotDrivesData(tableDataMap.plotDrives)
    : undefined;

  return {
    cutFills: buildTableItemLibrary(CUT_FILLS_REFERENCES, resourceCodeMap, tableDataMap?.cutFills),
    foundations: buildTableItemLibrary(FOUNDATIONS_REFERENCES, resourceCodeMap, tableDataMap?.foundations, false, false, foundationsDynamicResolver),
    drainage: buildTableItemLibrary(DRAINAGE_REFERENCES, resourceCodeMap, tableDataMap?.drainage, false, false, drainageDynamicResolver),
    plotDrives: applyPlotDrivesCodeOverrides(
      buildTableItemLibrary(PLOT_DRIVES_REFERENCES, resourceCodeMap, plotDrivesSemanticData, false, false, plotDrivesDynamicResolver),
      groundworksContext?.plotDrivesCodeOverrides,
      resourceCodeMap,
    ),
    garden: buildTableItemLibrary(GARDEN_REFERENCES, resourceCodeMap, tableDataMap?.garden),
    plotPavings: buildTableItemLibrary(PLOT_PAVINGS_REFERENCES, resourceCodeMap, tableDataMap?.plotPavings),
    scaffold: buildTableItemLibrary(SCAFFOLD_REFERENCES, resourceCodeMap, tableDataMap?.scaffold),
    plotService: applyPlotServiceApartmentsMultiple(
      buildTableItemLibrary(PLOT_SERVICE_REFERENCES, resourceCodeMap, tableDataMap?.plotService),
      groundworksContext?.plotServiceConfig,
    ),
    retainingWalls: buildTableItemLibrary(RETAINING_WALLS_REFERENCES, resourceCodeMap, tableDataMap?.retainingWalls, true, true, retainingWallsDynamicResolver),
    geoToberWall: buildTableItemLibrary(GEO_TOBER_WALL_REFERENCES, resourceCodeMap, tableDataMap?.geoToberWall, true, true),
  };
}
